import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getRememberedLocale } from "@/lib/authMessages";
import { makeT } from "@/lib/i18n";

/*
 * Gates every protected route behind the account prerequisites that apply to
 * every user:
 *   1. authenticated              → else /login
 *   2. !must_change_password      → else /change-password
 *   3. privacy consent accepted   → else /consent
 *   4. profile_completed          → else /setup
 *
 * Beta approval is intentionally NOT a global prerequisite. New accounts may
 * use the Free tier without a beta code. Beta Tester access is an entitlement
 * assigned separately from Admin.
 */

const CHAIN = [
  { path: "/change-password", check: (u) => !u.must_change_password },
  { path: "/consent",         check: (u) => !!u?.privacy_consent?.accepted },
  { path: "/setup",           check: (u) => u.profile_completed },
];

export default function ProtectedRoute({ children, allow = [] }) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div
        className="flex min-h-[60vh] items-center justify-center text-sm text-[color:var(--text-secondary)]"
        data-testid="auth-loading"
      >
        {/* The account isn't loaded yet: the language last used on this device. */}
        {makeT(getRememberedLocale())("iv.loading")}
      </div>
    );
  }

  if (status !== "authenticated" || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  for (const step of CHAIN) {
    if (allow.includes(step.path)) continue;
    if (!step.check(user)) {
      return <Navigate to={step.path} replace />;
    }
  }

  return children;
}
